import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { ServiceTypeForm } from "@/components/admin/commercial/service-type-form";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Blocks, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ServiceTypeRow {
  id: string;
  key: string;
  label: string;
  isActive: boolean;
  xeroRevenueAccountCode: string | null;
}

export default function ServiceTypesPage() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceTypeRow | null>(null);

  const { data, isLoading } = useQuery<ServiceTypeRow[]>({
    queryKey: ["/api/service-types"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const createMutation = useMutation({
    mutationFn: async (payload: ServiceTypeRow) => {
      const response = await apiRequest("POST", "/api/service-types", payload);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/service-types"] });
      setDialogOpen(false);
      toast({ title: "Service type created" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Partial<ServiceTypeRow> }) => {
      const response = await apiRequest("PATCH", `/api/service-types/${id}`, payload);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/service-types"] });
      setDialogOpen(false);
      setEditing(null);
      toast({ title: "Service type updated" });
    },
  });

  const columns: ColumnDef<ServiceTypeRow>[] = [
    { key: "label", header: "Label", cell: (row) => row.label },
    { key: "key", header: "Key", cell: (row) => row.key },
    {
      key: "xero",
      header: "Xero account",
      cell: (row) => row.xeroRevenueAccountCode ?? "—",
    },
    {
      key: "active",
      header: "Active",
      cell: (row) => (row.isActive ? "Yes" : "No"),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Service Types"
        description="Controlled taxonomy and Xero mapping for commercial delivery"
        action={
          <Button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Service Type
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        onRowClick={(row) => {
          setEditing(row);
          setDialogOpen(true);
        }}
        emptyIcon={Blocks}
        emptyTitle="No service types"
        emptyDescription="Add a service taxonomy before classifying service items."
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Service Type" : "Create Service Type"}</DialogTitle>
          </DialogHeader>
          <ServiceTypeForm
            defaultValues={editing ? ({ ...editing, key: editing.key as any } as any) : undefined}
            onSubmit={(payload) => {
              if (editing) {
                updateMutation.mutate({ id: editing.id, payload });
                return;
              }
              createMutation.mutate(payload as ServiceTypeRow);
            }}
            isPending={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
